import { jwtDecode } from 'jwt-decode';
import moment from 'moment';

class Auth {
  static authenticateUser(token: string) {
    if (!this.isValidTokenString(token)) {
      this.deauthenticateUser();
      return;
    }

    localStorage.setItem('token', token.trim());
  }

  static isUserAuthenticated() {
    const expired = this.isTokenExpired();
    if (expired) {
      this.deauthenticateUser();
      return false;
    }

    const jwt = this.getJWT();

    if (!jwt || jwt.tokenVersion !== 2) {
      this.deauthenticateUser();
      return false;
    }

    return !!this.getToken();
  }

  static isTokenExpired() {
    const token = this.getJWT();
    if (token && token.exp) {
      return (
        moment
          .duration(moment(token.exp * 1000).diff(moment()))
          .asMilliseconds() < 5000
      );
    }
    return false;
  }

  static deauthenticateUser() {
    localStorage.removeItem('token');

    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('twofa:')) {
          toRemove.push(k);
        }
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  }

  static getToken() {
    const token = localStorage.getItem('token');

    if (!this.isValidTokenString(token)) {
      if (token !== null) {
        this.deauthenticateUser();
      }

      return null;
    }

    return token.trim();
  }

  static getAuthorizationHeader() {
    const token = this.getToken();

    return token
      ? { Authorization: `Bearer ${token}` }
      : {};
  }

  static getJWT(): JWTFields {
    const token = this.getToken();

    if (!token) {
      return {};
    }

    try {
      return jwtDecode<JWTFields>(token);
    } catch {
      this.deauthenticateUser();
      return {};
    }
  }

  static saveUsername(username: string) {
    localStorage.setItem('un', username);
  }

  static savePassword(password: string) {
    localStorage.setItem('pwd', password);
  }

  static getUsername() {
    return localStorage.getItem('un') ?? '';
  }

  static getPassword() {
    return localStorage.getItem('pwd') ?? '';
  }

  static isAdmin() {
    const permissions = this.getJWT().permissions;
    return permissions?.includes('ADMIN') ?? false;
  }

  static isOperator() {
    const permissions = this.getJWT().permissions;
    return permissions?.includes('OPERATOR') ?? false;
  }

  static isNotUser() {
    const permissions = this.getJWT().permissions;
    return permissions
      ? permissions.includes('OPERATOR') || permissions.includes('ADMIN')
      : false;
  }

  static is2FAComplete(): boolean {
    const jwt = this.getJWT();
    if (jwt && jwt.enabledAuth === false) return true;
    return !!(jwt && jwt.twoFactorOk === true);
  }

  static set2FAComplete(): void {
    // no-op on purpose
  }

  static getUserClients() {
    return this.getJWT().userClients ?? [];
  }

  static canAccessPricing() {
    return this.getJWT().accessPricing ?? false;
  }

  static canManageEvents() {
    return this.getJWT().manageEvents ?? false;
  }

  static canManageWarehouses() {
    return this.getJWT().manageWarehouses ?? false;
  }

  static canManageRepairs() {
    return this.getJWT().manageRepairs ?? false;
  }

  static canManageRepairBudget() {
    return this.getJWT().manageRepairBudget ?? false;
  }

  static canExecuteIssueTask() {
    return this.getJWT().issueTaskExecutor ?? false;
  }

  static isValidTokenString(token: string | null | undefined) {
    if (typeof token !== 'string') {
      return false;
    }

    const trimmedToken = token.trim();

    return (
      !!trimmedToken &&
      trimmedToken !== 'null' &&
      trimmedToken !== 'undefined' &&
      trimmedToken.split('.').length === 3
    );
  }

}

export interface Client {
  id: number;
  name: string;
}

export interface Addon {
  id: number;
  keyword: string;
}

export interface UserClient {
  id: number;
  client: Client;
}

export interface ClientAddon {
  id: number;
  addon: Addon;
}

export interface JWTFields {
  id?: number;
  exp?: number;
  addonKeyword?: string;
  language?: string;
  permissions?: string[];
  name?: string;
  surname?: string;
  username?: string;
  clientId?: number;
  userClients?: UserClient[];
  clientAddons?: ClientAddon[];
  namespace?: string;
  accessPricing?: boolean;
  manageEvents?: boolean;
  manageWarehouses?: boolean;
  manageRepairs?: boolean;
  manageRepairBudget?: boolean;
  issueTaskExecutor?: boolean;
  twoFactorOk?: boolean;
  enabledAuth?: boolean;
  tokenVersion?: number;
}

export default Auth;